import { ClockIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  formatTimeParts,
  parseTimeString,
  type TimeParts,
} from "@/shared/lib/datetime-picker";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { cn } from "@/shared/lib/utils";

const QUICK_TIMES: TimeParts[] = [
  { hour: 7, minute: 0 },
  { hour: 8, minute: 0 },
  { hour: 9, minute: 0 },
  { hour: 12, minute: 0 },
  { hour: 17, minute: 0 },
  { hour: 18, minute: 0 },
  { hour: 23, minute: 0 },
  { hour: 23, minute: 59 },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${String(hour).padStart(2, "0")} ໂມງ`,
}));

function buildMinuteOptions(step: number) {
  const options: { value: string; label: string }[] = [];
  for (let minute = 0; minute < 60; minute += step) {
    options.push({
      value: String(minute),
      label: `${String(minute).padStart(2, "0")} ນາທີ`,
    });
  }
  return options;
}

export type TimePickerProps = {
  value?: TimeParts | null;
  onChange?: (time: TimeParts | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minuteStep?: number;
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  allowClear?: boolean;
};

export function TimePicker({
  value,
  onChange,
  placeholder = "ເລືອກເວລາ",
  disabled,
  className,
  minuteStep = 5,
  buttonVariant = "outline",
  allowClear = true,
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const minuteOptions = React.useMemo(() => {
    const base = buildMinuteOptions(minuteStep);
    if (value && !base.some((o) => o.value === String(value.minute))) {
      return [
        ...base,
        {
          value: String(value.minute),
          label: `${String(value.minute).padStart(2, "0")} ນາທີ`,
        },
      ].sort((a, b) => Number(a.value) - Number(b.value));
    }
    return base;
  }, [minuteStep, value]);

  const label = value ? formatTimeParts(value) : null;

  const setParts = (next: Partial<TimeParts>) => {
    const hour = next.hour ?? value?.hour ?? 8;
    const minute = next.minute ?? value?.minute ?? 0;
    onChange?.({ hour, minute });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={buttonVariant}
          disabled={disabled}
          className={cn(
            "justify-start text-left font-normal tabular-nums",
            !label && "text-muted-foreground",
            className,
          )}
        >
          <ClockIcon />
          {label ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <p className="text-muted-foreground mb-2 text-xs font-medium">
          ເລືອກເວລາ
        </p>

        <div className="mb-3 grid grid-cols-4 gap-1.5">
          {QUICK_TIMES.map((time) => {
            const key = formatTimeParts(time);
            const active =
              value?.hour === time.hour && value?.minute === time.minute;
            return (
              <Button
                key={key}
                type="button"
                size="sm"
                variant={active ? "default" : "outline"}
                className="h-8 px-0 text-xs tabular-nums"
                onClick={() => {
                  onChange?.(time);
                  setOpen(false);
                }}
              >
                {key}
              </Button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <span className="text-muted-foreground text-xs">ຊົ່ວໂມງ</span>
            <SimpleSelect
              value={value != null ? String(value.hour) : undefined}
              options={HOUR_OPTIONS}
              placeholder="HH"
              className="w-full"
              onValueChange={(v) => {
                const parsed = parseTimeString(`${v}:00`);
                if (parsed) setParts({ hour: parsed.hour });
              }}
            />
          </div>
          <div className="space-y-1">
            <span className="text-muted-foreground text-xs">ນາທີ</span>
            <SimpleSelect
              value={value != null ? String(value.minute) : undefined}
              options={minuteOptions}
              placeholder="mm"
              className="w-full"
              onValueChange={(v) => {
                setParts({ minute: Number(v) });
              }}
            />
          </div>
        </div>

        <div className="mt-3 flex justify-end gap-2">
          {allowClear ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                onChange?.(null);
                setOpen(false);
              }}
            >
              ລ້າງ
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            onClick={() => setOpen(false)}
          >
            ຕົກລົງ
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
